// src/config/theme.js
import { createTheme, rem } from '@mantine/core';

export const theme = createTheme({
	// Primary colors
	primaryColor: 'navy',
	colors: {
		// Custom navy palette
		navy: [
			'#E7E9EF', // 0: Lightest
			'#C5CAD9', // 1
			'#9BA3BC', // 2
			'#717C9F', // 3
			'#4B5682', // 4
			'#2B3665', // 5: Primary
			'#232D59', // 6
			'#1B244C', // 7
			'#141B3F', // 8
			'#0D1232' // 9: Darkest
		],
		// Accent orange palette
		accent: [
			'#FFF3E6', // 0
			'#FFE0C2', // 1
			'#FFCD99', // 2
			'#FFB970', // 3
			'#FFA647', // 4
			'#FF931E', // 5: Primary accent
			'#E67A00', // 6
			'#CC6C00', // 7
			'#B35E00', // 8
			'#994F00' // 9
		]
	},

	// Font settings
	fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
	fontFamilyMonospace: 'Monaco, Courier, monospace',

	// Heading styles
	headings: {
		fontFamily: 'Inter, sans-serif',
		sizes: {
			h1: { fontSize: rem(36), lineHeight: '1.3', fontWeight: '600' },
			h2: { fontSize: rem(28), lineHeight: '1.35', fontWeight: '600' },
			h3: { fontSize: rem(24), lineHeight: '1.4', fontWeight: '600' },
			h4: { fontSize: rem(20), lineHeight: '1.45', fontWeight: '500' },
			h5: { fontSize: rem(16), lineHeight: '1.5', fontWeight: '500' },
			h6: { fontSize: rem(14), lineHeight: '1.5', fontWeight: '500' }
		}
	},

	// Default radius for components
	radius: {
		xs: rem(2),
		sm: rem(4),
		md: rem(8),
		lg: rem(16),
		xl: rem(24)
	},

	// Spacing scale
	spacing: {
		xs: rem(4),
		sm: rem(8),
		md: rem(16),
		lg: rem(24),
		xl: rem(32)
	},

	// Shadow variations
	shadows: {
		xs: `0 ${rem(1)} ${rem(2)} rgba(16, 24, 40, 0.05)`,
		sm: `0 ${rem(1)} ${rem(3)} rgba(16, 24, 40, 0.1)`,
		md: `0 ${rem(4)} ${rem(8)} rgba(16, 24, 40, 0.12)`,
		lg: `0 ${rem(8)} ${rem(16)} rgba(16, 24, 40, 0.14)`,
		xl: `0 ${rem(12)} ${rem(24)} rgba(16, 24, 40, 0.16)`
	},

	// Component default styles
	components: {
		Button: {
			defaultProps: {
				size: 'md',
				variant: 'filled'
			},
			styles: {
				root: {
					fontWeight: 500,
					'&:hover': {
						transform: 'translateY(-1px)'
					}
				}
			}
		},
		Input: {
			defaultProps: {
				size: 'md'
			},
			styles: {
				input: {
					'&:focus': {
						borderColor: 'var(--mantine-color-navy-5)'
					}
				}
			}
		},
		Card: {
			defaultProps: {
				padding: 'lg',
				radius: 'md'
			},
			styles: {
				root: {
					backgroundColor: 'var(--mantine-color-white)'
				}
			}
		},
		Alert: {
			styles: {
				title: {
					fontWeight: 600
				}
			}
		}
	},

	// Other theme properties
	other: {
		transition: {
			default: 'all 200ms ease',
			fast: 'all 100ms ease',
			slow: 'all 300ms ease'
		}
	}
});

// Optional: Default color scheme setup
theme.defaultProps = {
	colorScheme: 'light'
};

// Optional: Custom focus styles
theme.focusRing = 'auto';
theme.focusRingStyles = {
	styles: theme => ({
		outlineOffset: rem(2),
		outline: `${rem(2)} solid ${theme.colors.navy[5]}`
	}),
	resetStyles: () => ({ outline: 'none' })
};
